import { Component } from '@theme/component';

/**
 * Slide trigger web component. Toggles a CSS class on a container by ID when clicked.
 * Use as opener (adds class) or closer (removes class).
 *
 * @extends Component
 *
 * @attr {string} container-id - ID of the container element to toggle (without #).
 * @attr {'opener'|'closer'} trigger - "opener" adds the class, "closer" removes it.
 * @attr {string} [toggle-class="is-open"] - Class to add/remove on the container.
 */
export class SlideTriggerComponent extends Component {
  static get observedAttributes() {
    return ['container-id', 'trigger', 'toggle-class'];
  }

  /** @type {string} */
  #containerId = '';

  /** @type {'opener'|'closer'} */
  #trigger = 'opener';

  /** @type {string} */
  #toggleClass = 'is-open';

  /** @type {(e: Event) => void} */
  #boundHandleClick = () => {};

  connectedCallback() {
    super.connectedCallback();
    this.#readAttributes();
    if (!this.#containerId) {
      console.warn('slide-trigger: missing container-id attribute');
      return;
    }
    this.#boundHandleClick = this.#handleClick.bind(this);
    this.addEventListener('click', this.#boundHandleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#boundHandleClick);
    super.disconnectedCallback();
  }

  attributeChangedCallback(
    /** @type {string} */ name,
    /** @type {string | null} */ _oldValue,
    /** @type {string | null} */ newValue,
  ) {
    if (name === 'container-id') this.#containerId = newValue ?? '';
    if (name === 'trigger') this.#trigger = newValue === 'closer' ? 'closer' : 'opener';
    if (name === 'toggle-class') this.#toggleClass = newValue ?? 'is-open';
  }

  #readAttributes() {
    this.#containerId = this.getAttribute('container-id') ?? '';
    this.#trigger = this.getAttribute('trigger') === 'closer' ? 'closer' : 'opener';
    this.#toggleClass = this.getAttribute('toggle-class') ?? 'is-open';
  }

  /**
   * @param {Event} e
   */
  #handleClick(e) {
    e.preventDefault();
    const container = document.getElementById(this.#containerId);
    if (!container) {
      console.warn(`slide-trigger: container not found for id "${this.#containerId}"`);
      return;
    }
    if (this.#trigger === 'opener') {
      container.classList.add(this.#toggleClass);
    } else {
      container.classList.remove(this.#toggleClass);
    }
  }
}

if (!customElements.get('slide-trigger')) {
  customElements.define('slide-trigger', SlideTriggerComponent);
}
